import { Injector } from '@angular/core';
import { clone, identity, random, sortBy, uniq } from '@sama/micro-dash';
import { AbstractToolHelper } from '../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import {
  InteractiveBorderEventInfo,
  SubpointEventInfo,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { Dimensions } from '../../../cross-team-shared/types/misc';
import { VectorLayerEditMode } from '../../../cross-team-shared/types/tool-modes';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { RectangleSubpointTranslationHelper } from '../shapes/rectangle/rectangle-subpoint-translation-helper';
import { RectangleExpandContractHelper } from '../tools/rectangle/rectangle-expand-contract-helper';
import { VectorDragHandler } from '../tools/shared/drag-handlers/vector-drag-handler';
import { ShapeHelper } from './shape-helper';

export class RectangleHelper extends ShapeHelper {
  override allowsMLExtrapolation = true;

  override arePointsValid(points: Point2D[]) {
    const uniqueX = uniq(points.map((point) => point[0]));
    const uniqueY = uniq(points.map((point) => point[1]));
    return uniqueX.length === 2 && uniqueY.length === 2;
  }

  override getSingleSubpointDragHandler(
    eventInfo: SubpointEventInfo,
    _editMode: VectorLayerEditMode,
    injector: Injector,
  ) {
    return new VectorDragHandler(
      eventInfo.event,
      injector,
      new RectangleSubpointTranslationHelper(
        eventInfo.shapeStore,
        eventInfo.subpointIndex,
        injector,
      ),
      {
        restrictInteractions: true,
      },
    );
  }

  override getInteractiveBorderDragHandler(
    eventInfo: InteractiveBorderEventInfo,
    injector: Injector,
  ): AbstractToolHelper {
    return new VectorDragHandler(
      eventInfo.event,
      injector,
      new RectangleExpandContractHelper(
        eventInfo.lineIndex,
        injector,
        eventInfo.shapeStore,
      ),
      {
        restrictInteractions: true,
      },
    );
  }

  override getCursorForInteractiveBorder(editMode: string, lineIndex: number) {
    if (editMode === 'drag') {
      return lineIndex % 2 === 0 ? 'ns-resize' : 'ew-resize';
    } else {
      return undefined;
    }
  }

  override makeRandomPoints(imageSize: Dimensions): Point2D[] {
    const width = random(imageSize.width / 2);
    const height = random(imageSize.height / 2);
    const left = random(imageSize.width - width);
    const top = random(imageSize.height - height);

    return [
      [left, top],
      [left + width, top],
      [left + width, top + height],
      [left, top + height],
    ];
  }

  override getMinPoints() {
    return 4;
  }

  override pointsStateToAnswer(initialPoints: Point2D[]) {
    return swapBottomPoints(initialPoints);
  }

  override pointsAnswerToState(initialPoints: Point2D[]) {
    return swapBottomPoints(initialPoints);
  }

  override getNumberOfLines(points: Point2D[]) {
    return points.length;
  }

  override fixPoints(
    points: Point2D[],
    _startingPoints?: Point2D[],
  ): Point2D[] {
    const [left, right] = sortBy([points[0][0], points[1][0]], identity);
    const [top, bottom] = sortBy([points[0][1], points[2][1]], identity);
    return [
      [left, top],
      [right, top],
      [right, bottom],
      [left, bottom],
    ];
  }
}

export function swapBottomPoints(initialPoints: Point2D[]) {
  const points = clone(initialPoints);
  points[3] = initialPoints[2];
  points[2] = initialPoints[3];
  return points;
}
